import { SHOT_OUTCOME_LABELS } from '@golf/common';
import type { ShotOutcome } from '@golf/common';
import {
    AlertTriangle, ArrowLeft, CalendarDays, ChevronRight, Crosshair, Flag, Flame, Grid2X2, History, Pencil, PieChart, Scale, Target, Trophy, Wrench,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ClubLabel } from '../components/ClubLabel.tsx';
import { ListeClubs } from './training/ListeClubs.tsx';
import { CarteStat, Colonnes, GrandChiffre, JaugeFiabilite, pct } from '../components/Stat.tsx';
import { formatDurationShort } from '../lib/duration.ts';
import type { Club } from '../lib/equipment.ts';
import { listClubs } from '../lib/equipment.ts';
import type { RoundClub, RoundJeu, RoundPutting, RoundStats } from '../lib/rounds.ts';
import { getRoundStats } from '../lib/rounds.ts';
import { useAuth } from '../lib/auth.tsx';
import { useSettings } from '../lib/settings.tsx';
import type { Stats } from '../lib/training.ts';
import { getStats } from '../lib/training.ts';
import type { ComparisonRow } from './training/ComparisonBars.tsx';
import { ShotIcon } from './training/ShotIcon.tsx';
import { MODES } from '../lib/modes.ts';
import { AteliersMultiples } from './training/AteliersMultiples.tsx';
import { ClubsDansLeTemps } from './training/ClubsDansLeTemps.tsx';
import { CourbeComparable } from './training/CourbeComparable.tsx';

/* Le détail que l'accueil résume.
 *
 * ── Pourquoi un écran à part ─────────────────────────────────────────────
 *
 * L'accueil montre un aperçu de chaque moitié de l'application — technique,
 * score — pour qu'on sache où on en est sans avoir à chercher. Le détail
 * complet n'a pas sa place là : dix tours en courbe, la carte de fiabilité de
 * chaque club, la liste des fautes, c'est une lecture qu'on vient chercher,
 * pas une qu'on subit à chaque ouverture.
 *
 * ── Parcours et practice en bascule, pas en deux pages ──────────────────────
 *
 * Ce sont deux facettes de la même progression, jamais deux sujets séparés :
 * le practice explique pourquoi le score bouge.
 */

const FAULTS: Exclude<ShotOutcome, 'center'>[] = ['slice', 'hook', 'top', 'fat', 'shank', 'miss'];

/** Ce que la faute signifie, en une ligne.
 *
 *  Factuel, jamais prescriptif : dire à quoi correspond le geste aide à
 *  reconnaître ce qui vient de se passer, alors qu'un conseil technique donné
 *  sans voir le joueur ferait plus de mal que de bien. */
const FAULT_MEANING: Record<Exclude<ShotOutcome, 'center'>, string> = {
    slice: 'La balle part droit puis s\'échappe vers l\'extérieur.',
    hook: 'La balle part droit puis se referme vers l\'intérieur.',
    top: 'La balle est touchée trop haut : elle file à ras de terre.',
    fat: 'Le club prend le sol avant la balle : la distance s\'effondre.',
    shank: 'La balle part du talon du club et fuse de côté.',
    // Sur le parcours, « Coup raté » enregistre ceci tant que le joueur n'a
    // rien précisé : la définition doit couvrir les deux, sans quoi le
    // lexique promettrait une lecture que la donnée ne porte pas.
    miss: 'Un coup manqué, sans plus de détail : balle non frappée, trajectoire inexploitable, ou faute non précisée.',
};

const share = (n: number, total: number): number =>
    total > 0 ? Math.round((n * 1000) / total) / 10 : 0;

/** Une phrase de régularité, construite à partir de ce qui est vrai.
 *
 *  Assemblée plutôt que choisie dans une liste de modèles : les cas se
 *  combinent (jamais venu, venu hier, trois semaines d'affilée) et un arbre de
 *  variantes finirait par en oublier un. */
function rhythmSentence(o: Stats['overview']): string {
    if (!o.lastSessionAt) return 'Aucun exercice terminé pour le moment.';
    const days = Math.floor((Date.now() - new Date(o.lastSessionAt).getTime()) / 86_400_000);
    const when = days === 0 ? "aujourd'hui" : days === 1 ? 'hier' : `il y a ${days} jours`;
    const month = o.sessions30d === 0
        ? 'Rien sur les 30 derniers jours'
        : `${o.sessions30d} entraînement${o.sessions30d > 1 ? 's' : ''} sur les 30 derniers jours`;
    return `${month}, la dernière ${when}.`;
}

/** L'écart au par, tel qu'on l'annonce au club-house.
 *
 *  La séparation décimale est FRANÇAISE. Un écart de tour est entier — la
 *  question ne se posait pas — mais la moyenne des trois derniers ne l'est
 *  pas, et « +8.7 » s'affichait à côté de « 17,3 putts » : deux notations
 *  pour deux nombres qui se lisent d'un même regard. */
function fmtEcart(n: number | null): string {
    if (n == null) return '—';
    if (n === 0) return 'par';
    const abs = Math.abs(n).toLocaleString('fr-FR', { maximumFractionDigits: 1 });
    return n > 0 ? `+${abs}` : `−${abs}`;
}

const jour = (iso: string): string =>
    new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/** En dessous, on ne prononce rien : trois trous ne font pas un diagnostic. */
const TROUS_MINI_POUR_JUGER = 18;

/** En dessous, on n'annonce même pas un taux : cinq tentatives, c'est une
 *  poignée, et « 0 % » sur trois départs décrirait surtout le hasard. */
const ESSAIS_MINI_POUR_UN_TAUX = 5;

/** Le niveau de référence d'un amateur correct, en pourcentage.
 *
 *  Trente pour cent de greens en régulation et trente pour cent de sauvetages :
 *  ce sont les ordres de grandeur du golf amateur, très loin des soixante-cinq
 *  et soixante du circuit professionnel. Ils servent d'ORIGINE au verdict — ce
 *  qu'on lit n'est pas un taux dans l'absolu, mais l'avance de l'un sur
 *  l'autre. */
const REFERENCE_AMATEUR = 30;

/* Un repère et sa jauge, sur sa pleine échelle.
 *
 * La jauge de fiabilité du practice ne convient pas ici : ses seuils sont
 * ceux d'un taux de centrage, où soixante-dix pour cent est bon. Trente pour
 * cent de greens en régulation est un très honnête résultat d'amateur, et la
 * même jauge l'aurait peint en rouge. */
function Repere({ mot, part, sur, ton, note }: {
    mot: string; part: number | null; sur: string; ton: string; note: string;
}) {
    return (
        <div>
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-sm font-bold">{mot}</span>
                <span className="font-score text-lg leading-none tabular-nums"
                    style={part == null ? { opacity: 0.4 } : { color: ton }}>
                    {part == null ? '—' : pct(Math.round(part * 10) / 10)}
                </span>
            </div>
            <span className="mt-1 flex h-2.5 overflow-hidden rounded-full"
                style={{ background: 'color-mix(in oklab, var(--tuile-encre) 16%, transparent)' }}>
                {part != null && (
                    <span className="block h-full rounded-full"
                        style={{ width: `${Math.max(2, Math.min(100, part))}%`, background: ton }} />
                )}
            </span>
            <p className="mt-1 text-xs opacity-60">{sur} · {note}</p>
        </div>
    );
}


/* D'où un club est joué, sur le parcours.
 *
 * ── Les couleurs sont celles du pavé de zones ───────────────────────────────
 *
 * Ce sont les mêmes que celles qu'on touche en jouant, quand on dit où la balle
 * est tombée : le vert du fairway, le vert sombre du rough. Le joueur n'a donc
 * rien de neuf à apprendre ici.
 *
 * Le sable fait exception : son jeton est un beige très clair, parfait derrière
 * un libellé sombre et illisible en barre pleine sur le papier de la carte. On
 * prend son ENCRE, qui reste de la même famille et tient au soleil.
 *
 * Et le nom de chaque zone est ÉCRIT à côté de sa barre : la couleur confirme,
 * elle ne porte rien seule.
 */
const ZONES_DE_JEU = [
    { cle: 'tee', mot: 'Départ', ton: 'var(--brass-ink)' },
    { cle: 'fairway', mot: 'Fairway', ton: 'var(--zone-fairway)' },
    { cle: 'rough', mot: 'Rough', ton: 'var(--zone-rough)' },
    { cle: 'bunker', mot: 'Bunker', ton: 'var(--zone-bunker-encre)' },
] as const;

/** Une barre horizontale étiquetée. Rien n'est tracé à zéro : une barre vide
 *  occupe une ligne pour dire qu'il ne s'est rien passé. */
function BarreZone({ mot, part, coups, ton }: {
    mot: string; part: number; coups: number; ton: string;
}) {
    if (coups === 0) return null;
    return (
        <div className="flex items-center gap-2">
            <span className="w-16 shrink-0 text-xs font-semibold opacity-70">{mot}</span>
            <span className="flex h-2 min-w-0 flex-1 overflow-hidden rounded-full"
                style={{ background: 'color-mix(in oklab, var(--tuile-encre) 12%, transparent)' }}>
                <span className="block h-full rounded-full"
                    style={{ width: `${Math.max(3, part)}%`, background: ton }} />
            </span>
            <span className="font-score w-10 shrink-0 text-right text-xs tabular-nums opacity-70">
                {Math.round(part)} %
            </span>
        </div>
    );
}

/* Les clubs du parcours.
 *
 * Le practice dit ce qu'un club REND quand on le travaille ; ceci dit ce qu'on
 * en FAIT en jouant — le driver sorti cinq fois, le wedge qui ne joue que
 * depuis le sable. Deux lectures différentes, et c'est pour cela qu'elles ne
 * partagent pas de bloc.
 *
 * Les clubs sortent du PLUS JOUÉ au moins joué, ce qui est une exception dans
 * l'application — partout ailleurs c'est l'ordre du sac, pour qu'on vise une
 * position connue. Ici le classement est la réponse à la question posée : avec
 * quoi ai-je joué ce tour ? Le serveur trie ; l'écran ne fait que suivre.
 *
 * Les quatre positions ne totalisent pas les coups du club, et c'est normal :
 * un putt part du green, un coup joué après un drop part d'un endroit que
 * personne n'a saisi. La note sous le bloc le dit.
 */
function CarteClubs({ clubs }: { clubs: RoundClub[] }) {
    const total = clubs.reduce((n, c) => n + c.shots, 0);
    if (total === 0) return null;

    return (
        <CarteStat icone={<Target size={13} aria-hidden="true" />} titre="Les clubs du parcours"
            note="Part des coups du tour, puis d’où chaque club est joué · un putt part du green">
            <ul className="space-y-4">
                {clubs.map((c) => (
                    <li key={c.clubId}>
                        <div className="flex items-baseline justify-between gap-2">
                            <ClubLabel club={c} className="text-sm font-bold" />
                            <span className="shrink-0 text-xs tabular-nums opacity-70">
                                {c.shots} coup{c.shots > 1 ? 's' : ''}
                                {' · '}
                                <span className="font-score">{Math.round((c.shots * 100) / total)} %</span>
                            </span>
                        </div>
                        <span className="mt-1 flex h-2.5 overflow-hidden rounded-full"
                            style={{ background: 'color-mix(in oklab, var(--tuile-encre) 16%, transparent)' }}>
                            <span className="block h-full rounded-full"
                                style={{
                                    width: `${Math.max(2, (c.shots * 100) / total)}%`,
                                    background: 'var(--color-primary)',
                                }} />
                        </span>
                        <div className="mt-2 space-y-1 pl-1">
                            {ZONES_DE_JEU.map((z) => (
                                <BarreZone key={z.cle} mot={z.mot} ton={z.ton}
                                    coups={c[z.cle]}
                                    part={(c[z.cle] * 100) / c.shots} />
                            ))}
                        </div>
                    </li>
                ))}
            </ul>
        </CarteStat>
    );
}

/* Le sable.
 *
 * UN nombre, pas deux : les bunkers qu'il a fallu quitter. Compter à côté les
 * « coups pour en sortir » paraissait symétrique et n'aurait rien dit — un coup
 * qui ne sort pas du sable y re-atterrit, donc il compte comme une entrée de
 * plus, et les deux totaux se suivent mécaniquement.
 *
 * Ce qui renseigne, c'est l'ÉCHEC : un coup joué depuis le sable qui y reste.
 * C'est la question qu'on se pose en rangeant son wedge — en suis-je sorti du
 * premier coup ?
 */
function CarteSable({ sable }: { sable: { entres: number; echecs: number } }) {
    if (sable.entres === 0) return null;
    return (
        <CarteStat icone={<Flag size={13} aria-hidden="true" />} titre="Le sable"
            note={sable.echecs > 0
                ? `${sable.echecs} coup${sable.echecs > 1 ? 's' : ''} joué${sable.echecs > 1 ? 's' : ''} du sable y ${sable.echecs > 1 ? 'sont restés' : 'est resté'}`
                : 'Sorti du premier coup à chaque fois'}>
            <div className="text-center">
                <GrandChiffre valeur={String(sable.entres)}
                    mot={`bunker${sable.entres > 1 ? 's' : ''} à quitter`}
                    ton={sable.echecs > 0 ? 'var(--color-error)' : undefined} />
            </div>
        </CarteStat>
    );
}

/* Où partent les gros scores.
 *
 * ── Pourquoi les trous à +2 et pire ─────────────────────────────────────────
 *
 * On ne perd pas son score régulièrement sur dix-huit trous : on le perd sur
 * quatre ou cinq. Une moyenne les dilue dans les pars, et c'est justement ce
 * qu'il faut isoler pour savoir quoi travailler.
 *
 * ── Les trois parts s'additionnent EXACTEMENT ───────────────────────────────
 *
 * Un par, c'est (par − 2) coups pour atteindre le green et deux putts. Tout ce
 * qui dépasse tombe donc dans l'une de trois cases, sans reste et sans
 * recouvrement : une pénalité, un coup de trop avant le green, un putt de trop.
 *
 * C'est ce qui sépare cette carte d'un comptage de causes, où un trou à la fois
 * pénalisé et trois-putté serait compté deux fois et où les parts ne voudraient
 * plus rien dire ensemble. Ici chaque coup perdu a un responsable, et un seul.
 *
 * ── L'ordre des parts ne change jamais ──────────────────────────────────────
 *
 * Jeu long, pénalités, putting — l'ordre d'un trou tel qu'on le joue. Trier par
 * taille rendrait la barre plus jolie et deux tours incomparables : la même
 * couleur ne serait plus au même endroit.
 */
function CarteGrosScores({ gros }: { gros: RoundStats['grosScores'] }) {
    if (gros.holes === 0 || gros.lost === 0) return null;

    const parts = [
        { mot: 'Jeu long', n: gros.longGame, ton: 'var(--zone-fairway)' },
        { mot: 'Pénalités', n: gros.penalties, ton: 'var(--brique-bord)' },
        { mot: 'Putting', n: gros.putting, ton: 'var(--brass-ink)' },
    ].filter((p) => p.n > 0);

    return (
        <CarteStat icone={<AlertTriangle size={13} aria-hidden="true" />}
            titre="Où partent les gros scores"
            note={`Trous rendus à +2 ou pire · ${gros.holes} sur ${gros.playedHoles} joués`}>
            <div className="grid grid-cols-2 gap-2 text-center">
                <GrandChiffre valeur={String(gros.lost)}
                    mot={`coup${gros.lost > 1 ? 's' : ''} perdu${gros.lost > 1 ? 's' : ''}`}
                    ton="var(--color-error)" />
                <GrandChiffre valeur={String(gros.holes)}
                    mot={`trou${gros.holes > 1 ? 's' : ''} concerné${gros.holes > 1 ? 's' : ''}`} />
            </div>

            {/* Une seule barre, découpée : les parts forment un tout, et trois
                barres séparées auraient laissé croire à trois mesures
                indépendantes. */}
            <span className="mt-4 flex h-3 overflow-hidden rounded-full"
                style={{ background: 'color-mix(in oklab, var(--tuile-encre) 16%, transparent)' }}>
                {parts.map((p) => (
                    <span key={p.mot} className="block h-full"
                        style={{ width: `${(p.n * 100) / gros.lost}%`, background: p.ton }} />
                ))}
            </span>

            {/* Le nom de chaque part est ÉCRIT avec son nombre : la couleur
                confirme le découpage, elle ne porte rien seule. */}
            <ul className="mt-3 space-y-1.5">
                {parts.map((p) => (
                    <li key={p.mot} className="flex items-baseline gap-2 text-sm">
                        <span className="size-2.5 shrink-0 rounded-full"
                            style={{ background: p.ton }} aria-hidden="true" />
                        <span className="min-w-0 flex-1 font-semibold">{p.mot}</span>
                        <span className="font-score shrink-0 tabular-nums">
                            {p.n} coup{p.n > 1 ? 's' : ''}
                        </span>
                    </li>
                ))}
            </ul>

            {(gros.throughBunker > 0 || gros.puttingSaved > 0) && (
                <p className="mt-3 text-xs opacity-60">
                    {gros.throughBunker > 0 && (
                        /* Le bunker est du CONTEXTE et non une part : il ne
                           coûte pas un nombre fixe de coups, et le compter
                           aurait fait deux fois le même coup avec le jeu long. */
                        /* « Un » et non « 1 » : un chiffre ouvre mal une phrase,
                           et celle-ci se lit, elle ne se compte pas. */
                        <>
                            {gros.throughBunker === 1
                                ? 'Un de ces trous est passé'
                                : `${gros.throughBunker} de ces trous sont passés`} par un bunker.
                        </>
                    )}
                    {gros.puttingSaved > 0 && (
                        <>
                            {' '}Vos putts en ont rendu {gros.puttingSaved} au passage.
                        </>
                    )}
                </p>
            )}
        </CarteStat>
    );
}

/* Où se perdent les coups.
 *
 * Les repères sont ceux du golf amateur, et ils sont ÉCRITS à côté de chaque
 * jauge plutôt que cachés dans un code couleur : trente pour cent de greens en
 * régulation n'évoque rien tant qu'on ne sait pas qu'un joueur de series le
 * double et qu'un débutant en est loin. Une couleur sans barème ne fait que
 * distribuer des bons points.
 *
 * Le VERDICT lit le croisement, jamais une mesure seule. Manquer ses greens en
 * sauvant beaucoup, c'est tenir son score grâce au petit jeu — le travail est
 * dans le swing. Toucher ses greens sans que le score suive désigne le
 * contraire. Et en dessous de dix-huit trous saisis, on ne prononce rien : un
 * diagnostic tiré de trois trous ferait travailler le mauvais geste. */
function CarteJeu({ jeu, unTour }: { jeu: RoundJeu; unTour: boolean }) {
    /* Pas de taux sous cinq tentatives : le compte brut reste écrit sous la
       jauge, mais un pourcentage tiré de trois essais se lirait comme une
       mesure alors qu'il n'en est pas une. */
    const part = (n: number, total: number): number | null =>
        (total >= ESSAIS_MINI_POUR_UN_TAUX ? (n * 100) / total : null);
    const fir = part(jeu.fairways, jeu.teeHoles);
    const gir = part(jeu.greens, jeu.greenHoles);
    const sauv = part(jeu.scrambles, jeu.scrambleHoles);

    /* Le verdict compare les DEUX AVANCES sur la référence, pas deux taux
       bruts. Des seuils absolus se trompaient dès que les deux mesures
       montaient ensemble : 33 % de greens et 72 % de sauvetages tombaient dans
       « équilibré », alors que le petit jeu porte visiblement le score. Ce qui
       renseigne, c'est lequel des deux est le plus en avance sur ce qu'on
       attend de lui. */
    /* Un SEUL tour ne tranche rien, même complet.
     *
     * Le verdict parle du jeu — « c'est le long jeu qui vous coûte des coups »
     * — et cela se dit sur une série de tours, pas sur une matinée où le vent
     * s'est levé au sixième. Borné à un tour, l'écran rend les mêmes chiffres
     * et se tait sur ce qu'ils voudraient dire.
     *
     * Et la réserve change de sens : « encore trop peu de trous saisis »
     * invitait à en jouer d'autres, ce qui n'a plus de sens pour un tour
     * terminé — il n'aura jamais plus de trous. */
    const assez = !unTour && jeu.greenHoles >= TROUS_MINI_POUR_JUGER;
    const ecart = gir == null || sauv == null ? null
        : (sauv - REFERENCE_AMATEUR) - (gir - REFERENCE_AMATEUR);
    const verdict = !assez || ecart == null ? null
        : ecart >= 15
            ? { mot: 'Votre petit jeu tient vos scores', ton: 'var(--brass-ink)',
                phrase: 'Vous manquez des greens et vous vous en sortez quand même. '
                    + 'C’est le long jeu qui vous coûte des coups — travaillez le swing.' }
            : ecart <= -15
                ? { mot: 'Le long jeu vous amène, le petit jeu ne finit pas',
                    ton: 'var(--brique-bord)',
                    phrase: 'Vous touchez des greens, mais les trous manqués ne se '
                        + 'rattrapent pas. Travaillez les abords et le putting.' }
                : { mot: 'Équilibré', ton: 'var(--color-primary)',
                    phrase: 'Ni le long jeu ni le petit jeu ne se détache : vos coups se '
                        + 'perdent un peu partout, ce qui est le cas normal.' };

    return (
        <CarteStat icone={<Target size={13} aria-hidden="true" />} titre="Où se perdent les coups"
            note={unTour
                ? `Sur les ${jeu.greenHoles} trous de ce tour · une matinée ne dit pas une tendance`
                : assez ? undefined
                    : `Encore trop peu de trous saisis pour trancher : ${jeu.greenHoles} sur ${TROUS_MINI_POUR_JUGER}`}>
            <div className="space-y-3">
                <Repere mot="Départ trouvé" part={fir} ton="var(--color-primary)"
                    sur={`${jeu.fairways} / ${jeu.teeHoles} trous`}
                    note="fairway atteint au premier coup, par 3 exclus" />
                <Repere mot="Green en régulation" part={gir} ton="var(--zone-green)"
                    sur={`${jeu.greens} / ${jeu.greenHoles} trous`}
                    note="green atteint en par moins deux · 30 % est un bon niveau amateur" />
                <Repere mot="Sauvetage" part={sauv} ton="var(--brass-ink)"
                    sur={`${jeu.scrambles} / ${jeu.scrambleHoles} trous`}
                    note="green manqué, trou rendu en par ou mieux · 30 % est un bon niveau" />
            </div>

            {verdict && (
                <div className="mt-3 rounded-field p-2.5"
                    style={{ backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                    <p className="text-sm font-bold" style={{ color: verdict.ton }}>{verdict.mot}</p>
                    <p className="mt-0.5 text-xs opacity-80">{verdict.phrase}</p>
                </div>
            )}
        </CarteStat>
    );
}

function CartePuttingParcours({ putting }: { putting: RoundPutting }) {
    const part = (putting.regulation * 100) / putting.holes;
    const max = Math.max(1, putting.onePutt, putting.twoPutt, putting.threePutt);
    return (
        <CarteStat icone={<Flag size={13} aria-hidden="true" />} titre="Putting"
            note={`Sur ${putting.holes} trous où les putts ont été notés · deux putts, c’est la régulation du green`}>
            <div className="grid grid-cols-2 gap-2 text-center">
                <GrandChiffre mot="en régulation"
                    valeur={pct(Math.round(part * 10) / 10)}
                    ton={part >= 70 ? 'var(--color-primary)'
                        : part >= 55 ? 'var(--brass-ink)' : 'var(--brique-bord)'} />
                {/* Les TROIS-PUTTS, et non la moyenne : celle-ci s'affiche déjà
                    en tête d'écran, au même centième, dans la carte Score. Ce
                    qu'elle ne dit pas, c'est le nombre de coups jetés sur le
                    green — et c'est celui-là qu'on va chercher. */}
                <GrandChiffre valeur={String(putting.threePutt)}
                    mot={putting.threePutt > 1 ? 'trois-putts' : 'trois-putt'}
                    ton={putting.threePutt > 0 ? 'var(--brique-bord)' : undefined} />
            </div>

            {/* Les trois issues d'un green, dans l'ordre où elles coûtent : un
                putt est gagné, deux est le contrat, trois est perdu. */}
            <div className="mt-4">
                <Colonnes hauteur="5rem" colonnes={[
                    { cle: '1', mot: '1 putt', valeur: String(putting.onePutt),
                        part: putting.onePutt / max, ton: 'var(--color-primary)' },
                    { cle: '2', mot: '2 putts', valeur: String(putting.twoPutt),
                        part: putting.twoPutt / max, ton: 'var(--tuile)' },
                    { cle: '3', mot: '3 putts et +', valeur: String(putting.threePutt),
                        part: putting.threePutt / max, ton: 'var(--brique-bord)' },
                ]} />
            </div>
        </CarteStat>
    );
}

function RoundsTab({ stats }: { stats: RoundStats }) {
    if (stats.rounds === 0) return <AucunTour />;

    /* Un seul tour regardé : les mêmes mesures, une autre échelle.
     *
     * Ce qui change n'est pas le calcul mais ce qu'on peut en dire. Une COURBE
     * d'un point ne montre aucune progression — elle montre une barre, et une
     * barre seule ne se compare à rien. Les moyennes, elles, restent justes :
     * la moyenne d'un tour est ce tour. */
    const unTour = stats.tour !== null;

    const d = stats.distribution;
    const max = Math.max(1, d.eagle, d.birdie, d.par, d.bogey, d.double);
    const totalTrous = d.eagle + d.birdie + d.par + d.bogey + d.double;

    // L'écart le plus large de la courbe fixe l'échelle : les dix tours se
    // lisent sur la même règle, pas chacun sur la sienne.
    const ecartMax = Math.max(1, ...stats.trend.map((p) => Math.abs(p.ecart)));

    /* Le dernier tour rendu, en tête de l'onglet.
     *
     * Symétrique du raccourci de l'onglet Practice, et pour la même raison :
     * l'écran dit ce que valent dix tours, et la question d'après est toujours
     * « et celui de dimanche ? ». La courbe le porte déjà — c'est son dernier
     * point — donc rien à demander de plus au serveur.
     *
     * Muet quand on regarde DÉJÀ un seul tour : le raccourci mènerait alors
     * à la page qu'on a sous les yeux. */
    const dernierTour = unTour ? null : stats.trend[stats.trend.length - 1] ?? null;

    return (
        <>
            {dernierTour && (
                <Link to={`/statistiques?tour=${encodeURIComponent(dernierTour.id)}`}
                    state={{ tab: 'rounds' }}
                    className="flex items-center gap-3 rounded-box bg-base-200 p-4 transition-colors hover:bg-base-300">
                    <History size={22} className="shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                        <span className="block text-[10px] font-bold uppercase tracking-[0.12em] opacity-50">
                            Dernier parcours
                        </span>
                        <span className="block truncate text-sm font-semibold">
                            {dernierTour.courseName}
                        </span>
                        <span className="block text-xs opacity-60">
                            {new Date(dernierTour.startedAt).toLocaleDateString('fr-FR', {
                                weekday: 'long', day: 'numeric', month: 'long',
                            })}
                            {` · ${dernierTour.totalStrokes} coups · ${fmtEcart(dernierTour.ecart)}`}
                        </span>
                    </span>
                    <ChevronRight size={20} className="shrink-0 opacity-50" />
                </Link>
            )}

            <CarteStat icone={<Flag size={13} aria-hidden="true" />}
                titre={unTour
                    ? 'Score de ce tour'
                    : `Score, ${stats.rounds} dernier${stats.rounds > 1 ? 's' : ''} tour${stats.rounds > 1 ? 's' : ''}`}
                note={unTour
                    ? 'Ce que vous avez rendu, et ce qu’il a coûté au green'
                    : 'Un tour par barre, du plus ancien au plus récent · la ligne, c’est le par'}>
                <div className="grid grid-cols-3 gap-2 text-center">
                    <GrandChiffre valeur={fmtEcart(stats.avgEcart)} mot="au par, moyenne"
                        ton={stats.avgEcart != null && stats.avgEcart <= 0
                            ? 'var(--color-success)' : 'var(--color-error)'} />
                    <GrandChiffre mot="putts / tour"
                        valeur={stats.avgPuttsRound != null
                            ? stats.avgPuttsRound.toFixed(1).replace('.', ',') : null} />
                    <GrandChiffre mot="putts / trou"
                        valeur={stats.avgPuttsHole != null
                            ? stats.avgPuttsHole.toFixed(2).replace('.', ',') : null} />
                </div>

                {/* Le par est une LIGNE, pas un sol.
                    Toutes les barres montaient depuis le bas et seule la
                    couleur disait de quel côté du par on était : le meilleur
                    tour du lot, à −2, s'affichait en filet de deux pixels et le
                    plus mauvais en colonne pleine. La hauteur disait donc
                    l'inverse de ce qu'on cherche à lire. Le zéro passe au
                    milieu : on monte au-dessus du par, on descend en dessous,
                    et la couleur ne fait plus que confirmer la direction.

                    Chaque barre porte son écart écrit. Trois tours, trois
                    nombres : à ce compte-là on ne surcharge rien, et un bon
                    tour reste lisible même quand la barre qui le porte est
                    courte parce qu'un mauvais tour du lot fixe l'échelle. */}
                <div className={`relative mt-4 ${unTour ? 'hidden' : ''}`} style={{ height: '5rem' }}>
                    <div className="absolute inset-0 flex items-stretch gap-1.5">
                        {stats.trend.map((p) => {
                            // Plafonné à 78 % de la demi-hauteur : le nombre est
                            // POSÉ sur la barre, donc une barre pleine pousse son
                            // étiquette hors du cadre. L'échelle reste la même pour
                            // tous, seule sa pente change.
                            const part = Math.max(10, (Math.abs(p.ecart) / ecartMax) * 78);
                            const sous = p.ecart <= 0;
                            const barre = (
                                <span className={`w-full ${sous ? 'rounded-b' : 'rounded-t'}`}
                                    style={{
                                        height: `${part}%`,
                                        background: sous ? 'var(--color-primary)' : 'var(--brique)',
                                    }} />
                            );
                            const nombre = (
                                <span className="font-score block py-0.5 text-center text-xs leading-none tabular-nums"
                                    style={{ color: 'var(--tuile-encre)' }}>
                                    {fmtEcart(p.ecart)}
                                </span>
                            );
                            return (
                                <div key={p.id} className="flex flex-1 flex-col"
                                    title={`${p.courseName} · ${jour(p.startedAt)} · ${fmtEcart(p.ecart)}`}>
                                    <span className="flex flex-1 flex-col justify-end">
                                        {!sous && <>{nombre}{barre}</>}
                                    </span>
                                    <span className="flex flex-1 flex-col justify-start">
                                        {sous && <>{barre}{nombre}</>}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                    {/* Tracée APRÈS les barres, donc par-dessus : dessinée avant,
                        elle disparaissait sous la première colonne posée
                        dessus — et la légende renvoyait à une ligne invisible. */}
                    <div className="pointer-events-none absolute inset-x-0 top-1/2 border-t border-dashed"
                        style={{ borderColor: 'var(--tuile-encre)', opacity: 0.5 }} />
                </div>
            </CarteStat>

            <CarteStat icone={<PieChart size={13} aria-hidden="true" />}
                titre="Répartition des scores"
                note={`Sur ${totalTrous} trous joués`}>
                {/* Les cinq colonnes de la carte de score, dans l'ordre du
                    meilleur au pire, et teintées comme elle : le vert descend
                    vers le rouge en passant par le papier du par. */}
                <Colonnes colonnes={([
                    ['eagle+', d.eagle, 'var(--color-primary)'],
                    ['birdie', d.birdie, 'var(--zone-green)'],
                    ['par', d.par, 'var(--tuile)'],
                    ['bogey', d.bogey, 'var(--brique)'],
                    // Le pas le plus sombre de la BRIQUE, et non le rouge
                    // d'erreur : les cinq colonnes forment une rampe qui
                    // descend du vert au rouge, et une rampe change de valeur,
                    // pas de famille. Le rouge d'erreur reste réservé à ce qui
                    // a échoué.
                    ['double+', d.double, 'var(--brique-bord)'],
                ] as const).map(([mot, n, ton]) => ({
                    cle: mot, mot, ton, valeur: String(n), part: n / max,
                }))} />
            </CarteStat>

            {/* Le PUTTING du parcours.
                La moyenne de putts figurait déjà en tête d'écran, mais une
                moyenne noie les trois-putts dans les un-putt : 2,0 de moyenne,
                c'est aussi bien neuf trous réguliers qu'un festival de un-putts
                payé par autant de trois-putts. Ce ne sont pas les mêmes coups
                perdus, ni le même travail à faire.

                La coupure est à DEUX putts — ce que le par d'un trou réserve au
                green. Au-delà, le coup est perdu et ne se rattrape pas
                ailleurs. */}
            {stats.jeu != null && stats.jeu.greenHoles > 0
                && <CarteJeu jeu={stats.jeu} unTour={unTour} />}
            <CarteGrosScores gros={stats.grosScores} />
            <CarteSable sable={stats.sable} />
            <CarteClubs clubs={stats.clubs} />

            {stats.putting != null && stats.putting.holes > 0 && (
                <CartePuttingParcours putting={stats.putting} />
            )}

            {stats.best && (
                <CarteStat icone={<Trophy size={13} aria-hidden="true" />} titre="Meilleur tour">
                    <div className="flex items-center gap-3">
                        <span className="min-w-0 flex-1">
                            <span className="block truncate font-bold">{stats.best.courseName}</span>
                            <span className="block text-xs opacity-60">
                                {new Date(stats.best.startedAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                            </span>
                        </span>
                        <span className="shrink-0 text-right">
                            <span className="font-score block text-2xl leading-none">{stats.best.totalStrokes}</span>
                            {/* Le meilleur tour n'est pas forcément un bon tour :
                                c'est le moins mauvais de ceux qu'on a joués. Le
                                vert était posé sans condition, et félicitait un
                                « +6 ». La couleur suit le par, comme partout
                                ailleurs sur l'écran. */}
                            <span className="block text-xs font-bold"
                                style={{
                                    color: (stats.best.ecart ?? 0) <= 0
                                        ? 'var(--color-success)' : 'var(--color-error)',
                                }}>
                                {fmtEcart(stats.best.ecart)}
                            </span>
                        </span>
                    </div>
                </CarteStat>
            )}

            <LienHistorique />
        </>
    );
}

/* La liste des tours se rejoint ICI, et nulle part ailleurs.
 *
 * Elle avait son entrée dans le tiroir, à côté de « Parcours » — deux lignes
 * voisines dont l'une servait à COMMENCER un tour et l'autre à en relire un,
 * distinguées par un possessif. Sur un menu qu'on ouvre gantée, c'est une
 * hésitation à chaque fois.
 *
 * Sa place est au bas de l'onglet Parcours : cette page donne la tendance, la
 * moyenne, le meilleur tour — le détail tour par tour en est la suite
 * naturelle, exactement comme l'historique des séances clôt l'onglet
 * Entraînement. Et parce que c'est désormais le SEUL chemin depuis le menu,
 * il est rendu aussi bien sous les statistiques que sous l'onglet vide. */
function LienHistorique() {
    return (
        <Link to="/parcours/historique"
            className="mt-3 flex items-center gap-3 rounded-box bg-base-200 p-4 transition-colors hover:bg-base-300">
            <History size={22} className="shrink-0 text-primary" />
            <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">Mes parcours joués</span>
                <span className="block text-xs opacity-60">
                    Cartes des scores et de quoi corriger une saisie
                </span>
            </span>
        </Link>
    );
}

/* Aucun tour terminé — le tout premier écran, celui où l'on comprend à quoi
 * sert l'application.
 *
 * Deux chemins y mènent : zéro tour rendu, ou des statistiques qui n'ont pas
 * pu être chargées. Le second retombait sur le contenu de l'onglet
 * Entraînement — les statistiques du practice s'affichant sous l'étiquette
 * « Parcours », un onglet montrant le contenu de l'autre.
 *
 * Le message d'attente ne suffisait plus une fois l'entrée retirée du
 * tiroir : sans le lien qui suit, un joueur ayant un tour EN COURS mais aucun
 * de terminé n'avait plus aucun chemin vers ses cartes. */
function AucunTour() {
    return (
        <>
            <div className="rounded-box bg-base-200 p-6 text-center">
                <Flag size={28} className="mx-auto mb-2 text-primary opacity-60" aria-hidden="true" />
                <p className="text-sm font-semibold">Aucun tour terminé</p>
                <p className="mt-1 text-xs opacity-60">
                    Tendance, moyenne et meilleur tour apparaîtront ici dès votre première
                    carte rendue.
                </p>
            </div>
            {/* Un tour commencé mais pas fini figure quand même dans la liste :
                le lien tient sa promesse même quand la page est vide. */}
            <LienHistorique />
        </>
    );
}

/** Un club, un taux, une jauge. Le sac entier depuis le premier jour.
 *
 *  Écrite une fois : l'écran l'affiche aussi bien quand aucune séance n'est
 *  terminée — les clubs, eux, ont déjà des balles — que dans le bilan complet,
 *  et les deux copies avaient déjà divergé d'un seuil et de trois couleurs. */
function CarteFiabilite({ clubs, max }: { clubs: ClubFiable[]; max: number }) {
    return (
        <CarteStat icone={<Pencil size={13} aria-hidden="true" />}
            titre="Fiabilité par club"
            note="Sac entier, toutes séances confondues, du plus sûr au moins sûr">
            <div className="space-y-2">
                {clubs.map((c) => (
                    <div key={c.id} className="flex items-center gap-3">
                        <span className="w-16 shrink-0 truncate text-sm font-bold">{c.label}</span>
                        <JaugeFiabilite taux={c.rate} part={(c.rate / max) * 100} />
                        <span className="font-score w-12 shrink-0 text-right text-base tabular-nums">
                            {pct(c.rate)}
                        </span>
                    </div>
                ))}
            </div>
        </CarteStat>
    );
}

type ClubFiable = Club & { rate: number };

/* D'où l'on vient, dit à l'écran qu'on ouvre : il en fera son lien de retour
 * plutôt que de renvoyer au menu dont personne n'arrive. */
const RETOUR_PRACTICE = { to: '/statistiques', label: 'Statistiques', state: { tab: 'practice' } };

function PracticeTab({ stats, clubs }: { stats: Stats | null; clubs: Club[] }) {
    const { user } = useAuth();
    const { unit } = useSettings();

    // Le sac entier n'a rien à faire dans la fiabilité : un club jamais
    // sorti n'a pas de fiabilité, il a une case vide, et l'afficher à zéro
    // mentirait autant que le taire.
    const reliability: ClubFiable[] = clubs
        .filter((c) => c.shotCount > 0)
        .map((c) => ({ ...c, rate: Math.round((c.centerCount * 100) / c.shotCount) }))
        .sort((a, b) => b.rate - a.rate);
    const reliabilityMax = Math.max(...reliability.map((c) => c.rate), 1);


    // La faute qui coûte le plus de balles sur la fenêtre récente.
    const dominant = stats && stats.recent.shots > 0
        ? FAULTS.map((f) => ({ f, n: stats.recent[f] }))
            .sort((a, b) => b.n - a.n)
            .find((x) => x.n > 0)
        : undefined;

    // Triées de la plus coûteuse à la moindre.
    //
    // L'ordre des boutons de saisie n'a rien à faire ici : sur le pavé il sert
    // la mémoire musculaire, sur un bilan il masquerait la seule chose qu'on y
    // cherche — par quoi commencer. La plus fréquente en haut, et la lecture
    // s'arrête à la première ligne pour qui est pressé.
    const faultRows: ComparisonRow[] = stats
        ? FAULTS.map((f) => ({
            key: f,
            label: SHOT_OUTCOME_LABELS[f],
            current: share(stats.recent[f], stats.recent.shots),
            previous: stats.previous.shots > 0
                ? share(stats.previous[f], stats.previous.shots) : null,
        }))
            .filter((r) => r.current > 0 || (r.previous ?? 0) > 0)
            .sort((a, b) => b.current - a.current || (b.previous ?? 0) - (a.previous ?? 0))
        : [];
    const faultMax = Math.max(20, Math.ceil(
        Math.max(0, ...faultRows.map((r) => Math.max(r.current, r.previous ?? 0))) / 10,
    ) * 10);
    const hasData = (stats?.overview.sessionsTotal ?? 0) > 0;

    if (!hasData) {
        return (
            <>
                {reliability.length > 0 && <CarteFiabilite clubs={reliability} max={reliabilityMax} />}
                <div className="rounded-box border border-base-300 bg-base-100 p-6 text-center shadow-sm">
                    <Crosshair size={28} className="mx-auto mb-2 text-primary opacity-60" aria-hidden="true" />
                    <p className="text-sm font-semibold">Aucune séance terminée</p>
                    <p className="mt-1 text-xs opacity-60">
                        Taux de balles centrées, fautes dominantes et fiabilité par club
                        apparaîtront ici dès votre première séance rendue.
                    </p>
                </div>
            </>
        );
    }

    // stats est nécessairement défini ici : hasData en dépend.
    const s = stats!;

    return (
        <>
            {/* UNE liste de clubs, et une seule.
                Deux cartes montraient les mêmes clubs — « dernier
                entraînement » pour le détail du jour, « fiabilité par club »
                pour le classement — et il fallait lire les deux pour se faire
                une idée d'un club. Elles n'en font plus qu'une : la fiabilité
                et la distance côte à côte, chacune avec sa pente, et le détail
                au bout du doigt. */}
            {/* Le dernier entraînement, au-dessus du sac.
                Cet écran répond « où j'en suis sur dix sorties » ; la question
                d'après est toujours « et celle d'hier ? », et elle demandait
                jusqu'ici de passer par l'historique pour choisir dans une liste
                dont la première ligne est la bonne. */}
            {s.lastSession && (
                <Link to={`/entrainement/${s.lastSession.id}`}
                    state={{ retour: RETOUR_PRACTICE }}
                    className="flex items-center gap-3 rounded-box bg-base-200 p-4 transition-colors hover:bg-base-300">
                    <History size={22} className="shrink-0 text-primary" />
                    <span className="min-w-0 flex-1">
                        {/* L'étiquette avant le nom : « Exercice libre » tout seul
                            ne dit pas de quelle sortie il s'agit, et la date en
                            dessous demande d'en déduire qu'on parle de la
                            dernière. */}
                        <span className="block text-[10px] font-bold uppercase tracking-[0.12em] opacity-50">
                            Dernier entraînement
                        </span>
                        <span className="block truncate text-sm font-semibold">
                            {s.lastSession.exerciseName
                                ?? MODES.find((m) => m.mode === s.lastSession!.mode)?.label
                                ?? 'Séance'}
                        </span>
                        <span className="block text-xs opacity-60">
                            {new Date(s.lastSession.startedAt).toLocaleDateString('fr-FR', {
                                weekday: 'long', day: 'numeric', month: 'long',
                            })}
                            {s.lastSession.shots > 0 && ` · ${s.lastSession.shots} balles · `
                                + `${Math.round((100 * s.lastSession.center) / s.lastSession.shots)} %`}
                        </span>
                    </span>
                    <ChevronRight size={20} className="shrink-0 opacity-50" />
                </Link>
            )}

            {s.clubs.length > 0 && (
                <CarteStat icone={<Wrench size={13} aria-hidden="true" />}
                    titre="Vos clubs"
                    note={`${s.recent.sessions > 1
                        ? `Sur vos ${s.recent.sessions} derniers entraînements`
                        : 'Sur votre dernier entraînement'} · touchez un club pour voir comment il se rate`}>
                    <ListeClubs stats={s} unit={unit} />
                </CarteStat>
            )}

            {/* TOUT LE SAC sur un cadre, à la place du taux d'ensemble.
                Le grand chiffre disait ce que valait le joueur ; celui-ci dit
                ce que vaut chaque club, et où il se situe dans le sac. */}
            <CarteStat icone={<Crosshair size={13} aria-hidden="true" />}
                titre="Vos clubs dans le temps"
                note="Un club ne fait que quelques balles par sortie · un 0 % et un 100 % voisins ne veulent rien dire">
                <ClubsDansLeTemps clubs={s.clubs} />
            </CarteStat>

            {/* La même fenêtre, débarrassée du programme.
                Juste APRÈS la courbe des taux et non à sa place : le taux brut
                reste ce qu'on a réellement fait, et un joueur a le droit de le
                lire. L'indice répond à l'autre question — est-ce que je
                progresse, indépendamment de ce que j'ai choisi de travailler. */}
            {s.comparable.length >= 2 && (
                <CarteStat icone={<Scale size={13} aria-hidden="true" />}
                    titre="Progression, à programme équivalent">
                    <CourbeComparable points={s.comparable} />
                </CarteStat>
            )}

            {s.ateliers.length > 0 && (
                <CarteStat icone={<Grid2X2 size={13} aria-hidden="true" />}
                    titre="Atelier par atelier"
                    note="Même échelle 0–100 % partout · le trait marque la moitié">
                    <AteliersMultiples ateliers={s.ateliers} />
                </CarteStat>
            )}

            {dominant && (
                <CarteStat icone={<ShotIcon outcome={dominant.f} mirror={user?.dominantHand === 'left'} size={13} />}
                    titre="Vos fautes les plus fréquentes">
                    {/* La faute dominante est un VERDICT, pas une ligne de
                        liste : c'est la seule chose de la carte sur laquelle on
                        peut agir demain. Elle prend la brique — la couleur de
                        ce qui a coûté — le nom en grand, et sa part en chiffre
                        de score. */}
                    <div className="flex items-center gap-3 rounded-field p-3"
                        style={{ backgroundColor: 'var(--brique)', color: 'var(--brique-encre)' }}>
                        <span className="shrink-0 rounded-field p-1.5"
                            style={{ backgroundColor: 'var(--color-base-100)', color: 'var(--brique-bord)' }}>
                            <ShotIcon outcome={dominant.f} mirror={user?.dominantHand === 'left'} size={30} />
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="font-display truncate text-xl font-bold leading-[1.15]">
                                {SHOT_OUTCOME_LABELS[dominant.f]}
                            </div>
                            <p className="mt-0.5 text-xs opacity-75">{FAULT_MEANING[dominant.f]}</p>
                        </div>
                        <div className="shrink-0 text-right">
                            <div className="font-score text-2xl leading-none">
                                {pct(share(dominant.n, s.recent.shots))}
                            </div>
                            <div className="mt-0.5 text-[9px] font-bold uppercase tracking-[0.12em] opacity-70">
                                des balles
                            </div>
                        </div>
                    </div>
                    {/* Les mêmes colonnes que la carte de score, parce que
                        c'est la même question : de quoi est fait le total.

                        UNE seule teinte, et non une par faute : les fautes ne
                        se classent pas de la meilleure à la pire, elles se
                        nomment. Six couleurs auraient suggéré un ordre qui
                        n'existe pas. Seule la dominante se détache, et le
                        pointillé dit où en était la période précédente. */}
                    {faultRows.length >= 2 && (
                        <div className="mt-4">
                            <Colonnes hauteur="5rem" colonnes={faultRows.map((r) => ({
                                cle: r.key,
                                mot: r.label.toLowerCase(),
                                // Le signe RESTE. Ce sont des parts de balles,
                                // et un « 14,3 » nu au sommet d'une colonne se
                                // lit comme un compte — or la carte d'à côté en
                                // affiche justement, des comptes. L'espace de
                                // pct() est insécable : « % » ne tombera pas
                                // seul sous la colonne.
                                valeur: pct(r.current),
                                part: r.current / faultMax,
                                ton: r.key === dominant.f ? 'var(--brique)' : 'var(--tuile)',
                                avant: r.previous != null ? r.previous / faultMax : null,
                            }))} />
                            <p className="mt-2 text-xs opacity-50">
                                Part des balles, pour que deux périodes de volume
                                différent restent comparables
                                {s.previous.shots > 0 && ' · le pointillé, c’est la période précédente'}
                            </p>
                        </div>
                    )}
                </CarteStat>
            )}

            <CarteStat icone={<CalendarDays size={13} aria-hidden="true" />} titre="Votre rythme">
                <div className="grid grid-cols-3 gap-2 text-center">
                    <GrandChiffre valeur={String(s.overview.sessionsTotal)}
                        mot={s.overview.sessionsTotal > 1 ? 'entraînements' : 'entraînement'} />
                    <GrandChiffre valeur={String(s.overview.shotsTotal)} mot="balles" />
                    <GrandChiffre mot="au practice"
                        valeur={s.overview.totalSeconds > 0
                            ? formatDurationShort(s.overview.totalSeconds * 1000)
                            : null} />
                </div>

                {/* La phrase prend le sable : posée à nu sous les chiffres, elle
                    se lisait comme leur légende alors qu'elle dit autre chose. */}
                <p className="mt-4 rounded-field px-3 py-2 text-sm"
                    style={{ backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                    {rhythmSentence(s.overview)}
                </p>

                {/* Le LAITON, couleur de ce qui est acquis dans l'application —
                    le vainqueur d'une partie, la carte rendue. Une série de
                    semaines en est une : elle ne se rattrape pas, elle se garde.
                    En vert, elle passait pour une statistique de plus. */}
                {s.overview.streakWeeks >= 2 && (
                    <p className="mt-2">
                        <span className="inline-flex items-center gap-1.5 rounded-field px-2.5 py-1 text-xs font-bold shadow-sm"
                            style={{ backgroundColor: 'var(--color-accent)', color: 'var(--color-accent-content)' }}>
                            <Flame size={13} aria-hidden="true" className="shrink-0" />
                            <span className="font-score text-sm leading-none">{s.overview.streakWeeks}</span>
                            {' semaines d’affil\u00e9e'}
                        </span>
                    </p>
                )}
            </CarteStat>

            {/* La pente. Un grand nombre suffit — c'est une valeur unique,
                pas une série : un graphique à un point ne dirait rien de
                plus, et prendrait dix fois la place. */}
            {/* On ne progresse pas avec un club qu'on ne sort jamais, et
                c'est le genre d'angle mort qu'aucune statistique de réussite
                ne peut signaler : un club absent n'a pas de mauvais chiffre,
                il n'en a aucun. */}
            {s.neglected.length > 0 && (
                <CarteStat icone={<Wrench size={13} aria-hidden="true" />} titre="Vos clubs oubliés"
                    note="Dans votre sac, mais pas sortis depuis un moment">
                    <ul className="space-y-1.5">
                        {s.neglected.map((c) => (
                            <li key={c.clubId}
                                className="flex items-baseline justify-between gap-3 rounded-field px-2.5 py-1.5 text-sm"
                                style={{ backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                                <ClubLabel club={c} className="text-sm" />
                                <span className="shrink-0 text-xs opacity-75">
                                    {c.lastPlayedAt
                                        ? `il y a ${Math.round((Date.now() - new Date(c.lastPlayedAt).getTime()) / 86_400_000)} jours`
                                        : 'jamais joué'}
                                </span>
                            </li>
                        ))}
                    </ul>
                </CarteStat>
            )}

            <Link to="/entrainement/historique"
                className="mt-3 flex items-center gap-3 rounded-box bg-base-200 p-4 transition-colors hover:bg-base-300">
                <History size={22} className="shrink-0 text-primary" />
                <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold">Historique des séances</span>
                    <span className="block text-xs opacity-60">
                        Le détail séance par séance, et la comparaison avec la précédente
                    </span>
                </span>
            </Link>
        </>
    );
}

export function Statistics() {
    // Les cartes « Détail » de l'accueil pointent ici avec l'onglet déjà
    // choisi — inutile de faire revoir le score à qui vient de cliquer sur
    // le bloc practice, et inversement.
    const location = useLocation();
    /* Le tour regardé vient de l'ADRESSE, et non d'un état de navigation.
     *
     * C'est ce qui rend le lien partageable et rechargeable : on revient sur
     * cet écran depuis la carte d'un tour, on recharge la page au club-house,
     * et on retrouve les mêmes chiffres. Un état porté par le routeur se serait
     * évaporé au premier rafraîchissement. */
    const tourFiltre = new URLSearchParams(location.search).get('tour');
    const initial = (location.state as { tab?: 'rounds' | 'practice' } | null)?.tab
        ?? 'rounds';
    // Filtrer sur un tour n'a de sens que sur l'onglet des parcours.
    const [tab, setTab] = useState<'rounds' | 'practice'>(tourFiltre ? 'rounds' : initial);
    const [roundStats, setRoundStats] = useState<RoundStats | null>(null);
    const [stats, setStats] = useState<Stats | null>(null);
    const [clubs, setClubs] = useState<Club[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        void Promise.all([getRoundStats(tourFiltre ?? undefined), getStats(), listClubs()])
            .then(([r, s, c]) => { setRoundStats(r); setStats(s); setClubs(c); })
            .finally(() => setLoading(false));
    }, [tourFiltre]);

    return (
        <div className="space-y-4 py-2">
            <header>
                <Link to="/" className="btn btn-ghost btn-sm mb-2 gap-2">
                    <ArrowLeft size={16} /> Accueil
                </Link>
                <h1 className="font-display text-2xl font-bold tracking-tight">Statistiques</h1>
            </header>

            {/* Le tour regardé, dit en toutes lettres.
                Sans ce bandeau, les chiffres d'un seul tour se lisent comme
                ceux de la saison — un 41 % de greens pris passerait pour un
                niveau alors que c'est une matinée. Et la sortie du filtre est
                là, à côté : c'est la seule façon de revenir à l'ensemble. */}
            {roundStats?.tour && (
                <div className="flex items-center gap-3 rounded-box p-3"
                    style={{ background: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                    <Flag size={16} className="shrink-0" aria-hidden="true" />
                    <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">
                            {roundStats.tour.courseName}
                        </span>
                        <span className="block text-xs opacity-70">
                            {new Date(roundStats.tour.startedAt).toLocaleDateString('fr-FR', {
                                weekday: 'long', day: 'numeric', month: 'long',
                            })}
                        </span>
                    </span>
                    <Link to="/statistiques" replace
                        className="btn btn-ghost btn-sm shrink-0 tap-target">
                        Tous mes tours
                    </Link>
                </div>
            )}

            {/* Deux onglets, en pastilles.
                L'actif est une carte BLANCHE posée sur le papier vert pâle du
                rail — le même rapport que les cartes de l'écran sur leur fond.
                Les onglets daisyUI donnaient un soulignement, qui se lit mal
                au soleil et ne dit pas qu'on peut appuyer. */}
            {/* Pas d'onglets quand on regarde UN tour : le practice n'a rien
                à voir avec la matinée qu'on vient de jouer, et l'onglet
                inviterait à un écran dont le bandeau ci-dessus ne parle pas. */}
            <div role="tablist" hidden={roundStats?.tour != null}
                className="flex gap-1 rounded-box bg-base-200 p-1">
                {([['rounds', 'Parcours'], ['practice', 'Practice']] as const).map(([cle, mot]) => (
                    <button key={cle} role="tab" type="button"
                        aria-selected={tab === cle}
                        onClick={() => setTab(cle)}
                        className={`flex-1 rounded-field py-2 text-sm font-bold transition-colors ${
                            tab === cle ? 'bg-base-100 shadow-sm' : 'opacity-60 hover:opacity-100'
                        }`}>
                        {mot}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-ring loading-lg text-primary" />
                </div>
            ) : tab === 'rounds' ? (
                roundStats ? <RoundsTab stats={roundStats} /> : <AucunTour />
            ) : (
                <PracticeTab stats={stats} clubs={clubs} />
            )}
        </div>
    );
}
